import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Tools.css';

const Tools: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'glossary' | 'tax'>('glossary');

  const glossaryTerms = [
    {
      term: "Asset Allocation",
      definition: "The process of dividing investments among different asset categories, such as stocks, bonds, and cash, to optimize the balance between risk and return based on an individual's goals, risk tolerance, and investment timeframe."
    },
    {
      term: "Diversification",
      definition: "An investment strategy that spreads risk by allocating investments across various financial instruments, industries, and other categories to reduce the impact of any single asset's poor performance."
    },
    {
      term: "Fiduciary",
      definition: "A person or organization that acts on behalf of another person or persons to manage assets. Fiduciaries are legally and ethically bound to act in their clients' best interests."
    },
    {
      term: "Risk Tolerance",
      definition: "An investor's ability and willingness to lose some or all of an original investment in exchange for greater potential returns. Risk tolerance is influenced by factors such as age, investment goals, income, and investment experience."
    },
    {
      term: "Compound Interest",
      definition: "Interest calculated on the initial principal and also on the accumulated interest of previous periods. This creates a snowball effect where interest earns interest, leading to exponential growth over time."
    },
    {
      term: "Dollar-Cost Averaging",
      definition: "An investment strategy where an investor divides the total amount to be invested across periodic purchases to reduce the impact of volatility on the overall purchase."
    },
    {
      term: "Emergency Fund",
      definition: "A savings account set aside for unexpected expenses or financial emergencies, typically containing 3-6 months of living expenses."
    },
    {
      term: "401(k)",
      definition: "A tax-advantaged retirement savings plan offered by employers where employees can contribute a portion of their pre-tax salary, often with employer matching contributions."
    }
  ];

  const taxResources = [
    {
      title: "IRS Official Website",
      description: "The official source for tax forms, publications, and guidance from the Internal Revenue Service.",
      url: "https://www.irs.gov",
      type: "Government Resource"
    },
    {
      title: "Tax Calendar",
      description: "Important tax dates and deadlines for individuals and businesses throughout the year.",
      url: "https://www.irs.gov/newsroom/tax-due-dates",
      type: "Planning Tool"
    },
    {
      title: "Form 1040 Instructions",
      description: "Detailed instructions for completing your individual income tax return.",
      url: "https://www.irs.gov/forms-pubs/about-form-1040",
      type: "Forms & Instructions"
    },
    {
      title: "Retirement Account Contribution Limits",
      description: "Current year contribution limits for 401(k), IRA, and other retirement accounts.",
      url: "https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-contributions",
      type: "Reference Guide"
    },
    {
      title: "Tax Deduction Checklist",
      description: "A comprehensive list of potential tax deductions for individuals and families.",
      url: "https://www.irs.gov/newsroom/heres-what-taxpayers-need-to-know-about-deductions",
      type: "Planning Tool"
    },
    {
      title: "State Tax Information",
      description: "Links to state tax agencies and resources for state-specific tax requirements.",
      url: "https://www.taxadmin.org/state-tax-agencies",
      type: "Reference Guide"
    }
  ];

  return (
    <div className="tools">
      {/* Hero Section */}
      <section className="tools-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Financial Tools & Resources</h1>
            <p className="hero-subtitle">
              Access our collection of financial tools, glossary terms, and tax resources 
              to help you better understand and manage your finances.
            </p>
          </div>
        </div>
      </section>

      {/* Tools Navigation */}
      <section className="section tools-content">
        <div className="container">
          <div className="tools-navigation">
            <button 
              className={`tab-button ${activeTab === 'glossary' ? 'active' : ''}`}
              onClick={() => setActiveTab('glossary')}
            >
              <span className="tab-icon">📚</span>
              Financial Glossary
            </button>
            <button 
              className={`tab-button ${activeTab === 'tax' ? 'active' : ''}`}
              onClick={() => setActiveTab('tax')}
            >
              <span className="tab-icon">📊</span>
              Tax Resources
            </button>
          </div>

          {/* Glossary Section */}
          {activeTab === 'glossary' && (
            <div className="tools-section">
              <div className="section-header">
                <h2>Financial Glossary</h2>
                <p>Understand key financial terms and concepts that are important for your financial planning journey.</p>
              </div>
              
              <div className="glossary-grid">
                {glossaryTerms.map((item, index) => (
                  <div key={index} className="glossary-card">
                    <h3 className="glossary-term">{item.term}</h3>
                    <p className="glossary-definition">{item.definition}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tax Resources Section */}
          {activeTab === 'tax' && (
            <div className="tools-section">
              <div className="section-header">
                <h2>Tax Resources</h2>
                <p>Access helpful tax-related links, forms, and information to assist with your tax planning and preparation.</p>
              </div>
              
              <div className="tax-resources-grid">
                {taxResources.map((resource, index) => (
                  <div key={index} className="tax-resource-card">
                    <div className="resource-type">{resource.type}</div>
                    <h3>{resource.title}</h3>
                    <p>{resource.description}</p>
                    <a 
                      href={resource.url} 
                      className="btn btn-secondary"
                      target={resource.url.startsWith('http') ? '_blank' : '_self'}
                      rel={resource.url.startsWith('http') ? 'noopener noreferrer' : ''}
                    >
                      Access Resource
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Calculators Section */}
      <section className="section section-alt calculators-section">
        <div className="container">
          <h2 className="section-title">Financial Calculators</h2>
          <p className="section-subtitle">
            Use these calculators to help plan for your financial goals and make informed decisions.
          </p>
          
          <div className="grid grid-cols-3">
            <div className="calculator-card">
              <div className="calculator-icon">🧮</div>
              <h3>Retirement Calculator</h3>
              <p>Estimate how much you need to save for retirement and track your progress toward your goals.</p>
              <button className="btn btn-primary" onClick={() => alert('Calculator coming soon!')}>Calculate</button>
            </div>
            
            <div className="calculator-card">
              <div className="calculator-icon">🏠</div>
              <h3>Mortgage Calculator</h3>
              <p>Calculate monthly mortgage payments and compare different loan scenarios.</p>
              <button className="btn btn-primary" onClick={() => alert('Calculator coming soon!')}>Calculate</button>
            </div>
            
            <div className="calculator-card">
              <div className="calculator-icon">📈</div>
              <h3>Investment Calculator</h3>
              <p>See how your investments could grow over time with compound interest.</p>
              <button className="btn btn-primary" onClick={() => alert('Calculator coming soon!')}>Calculate</button>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section tools-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Need Professional Guidance?</h2>
            <p>
              While these tools provide valuable insights, personalized financial planning 
              requires professional expertise. Let us help you create a comprehensive financial strategy.
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn btn-primary">
                Schedule a Consultation
              </Link>
              <Link to="/services" className="btn btn-secondary">
                View Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tools; 